import React, { useState, useEffect, useMemo } from "react";
import {
  HardHat, Camera, Layers, Clock, RefreshCw,
  BarChart3, Users, Activity, ShieldAlert,
  Sparkles, Eye, Maximize2, X,
  AlertTriangle, Bell, Thermometer, Shield
} from "lucide-react";
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie, Legend,
  ComposedChart, Line
} from "recharts";
import { Button, Table, Empty } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import axios from "../../utils/axiosDash";

dayjs.extend(relativeTime);

// ── DESIGN TOKENS ──────────────────────────────────────────────────────────
const C = {
  ink:          "#0F1623",
  inkMid:       "#3D4657",
  inkSoft:      "#7B8599",
  inkMute:      "#A8B0C0",
  steel:        "#3D63DD",
  steelLight:   "#EEF2FF",
  teal:         "#0EA5A8",
  tealLight:    "#E0F7F7",
  amber:        "#F59E0B",
  amberLight:   "#FEF3C7",
  success:      "#10B981",
  successLight: "#D1FAE5",
  violet:       "#8B5CF6",
  violetLight:  "#EDE9FE",
  danger:       "#EF4444",
  dangerLight:  "#FEE2E2",
  canvas:       "#F0F2F8",
  surface:      "#FFFFFF",
  surfaceAlt:   "#F7F8FC",
  border:       "#E4E7F0",
  borderSoft:   "#EFF1F7",
};

const CLASS_PALETTE = [
  C.steel, C.teal, C.amber, C.violet,
  "#EC4899", C.success, "#EF4444", "#F97316",
];

const colorForClass = (cls, list) =>
  CLASS_PALETTE[list.indexOf(cls) % CLASS_PALETTE.length];

const titleCase = (str = "") =>
  str.replace(/[_-]/g, " ").split(" ").filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const isViolation = (cls = "") => {
  const c = cls.toLowerCase();
  return c.includes("no_") || c.includes("without") ||
         c.includes("violation") || c.includes("missing") ||
         c.startsWith("no ");
};

// ── TOOLTIP ────────────────────────────────────────────────────────────────
const DateTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const parsed = dayjs(label, ["YYYY-MM-DD HH:00", "YYYY-MM-DD HH:mm"]);
  const displayDate = parsed.isValid()
    ? parsed.format("ddd, MMM D · h:mm A")
    : label;
  return (
    <div className="bg-[#0F1623] rounded-xl px-3.5 py-2.5 shadow-2xl border border-white/10 min-w-[180px]">
      <p className="font-bold text-white text-[11px] mb-2 uppercase tracking-[0.06em]">
        {displayDate}
      </p>
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-2 text-[#A8B0C0] text-xs my-0.5">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color || p.fill }} />
          <span>{p.name}:</span>
          <strong className="text-white">{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0F1623] rounded-xl px-3.5 py-2.5 shadow-2xl border border-white/10">
      <p className="font-bold text-white text-[11px] mb-2 uppercase tracking-[0.06em]">
        {label}
      </p>
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-2 text-[#A8B0C0] text-xs my-0.5">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color || p.fill }} />
          <span>{p.name}:</span>
          <strong className="text-white">{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

// ── IMAGE LIGHTBOX ─────────────────────────────────────────────────────────
const Lightbox = ({ open, onClose, src, alt, meta }) => {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[2000] bg-[#080C16]/90 backdrop-blur-sm flex items-center justify-center p-4 animate-[fadeIn_0.18s_ease]"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-[#0F1623] rounded-2xl overflow-hidden max-w-[780px] w-full shadow-2xl border border-white/10 animate-[slideUp_0.22s_cubic-bezier(0.34,1.56,0.64,1)]"
      >
        <img
          src={src}
          alt={alt}
          className="w-full max-h-[70vh] object-contain block bg-black"
          onError={e => { e.target.src = ""; e.target.alt = "Image unavailable"; }}
        />
        {meta && (
          <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-4">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="flex items-center gap-1.5 text-xs text-[#A8B0C0]">
                <Camera size={13} className="text-[#0EA5A8]" /> {meta.camera_id}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-[#A8B0C0]">
                <Clock size={13} className="text-[#F59E0B]" /> {dayjs(meta.timestamp).format("MMM D, h:mm A")}
              </span>
              {meta.ppe_classes?.length > 0 && (
                <span className="flex items-center gap-1.5 text-xs text-[#A8B0C0]">
                  <HardHat size={13} className="text-[#8B5CF6]" /> {meta.ppe_classes.map(titleCase).join(", ")}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-3.5 py-1.5 rounded-lg border border-white/20 bg-transparent text-[#A8B0C0] cursor-pointer text-xs"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── CHART MODAL ────────────────────────────────────────────────────────────
const ChartModal = ({ open, onClose, title, sub, icon: Icon, accent, children }) => {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[1000] bg-[#0A0E18]/80 backdrop-blur-sm flex items-center justify-center p-4 animate-[fadeIn_0.18s_ease]"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-[slideUp_0.22s_cubic-bezier(0.34,1.56,0.64,1)] border border-[#E4E7F0]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#EFF1F7] bg-[#F7F8FC]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}18` }}>
              <Icon size={18} color={accent} strokeWidth={2.2} />
            </div>
            <div>
              <div className="text-base font-extrabold text-[#0F1623] tracking-[-0.02em]">{title}</div>
              {sub && <div className="text-xs text-[#7B8599] mt-0.5">{sub}</div>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg border border-[#E4E7F0] bg-white cursor-pointer flex items-center justify-center transition-all hover:bg-[#EEF2FF] hover:border-[#3D63DD]"
          >
            <X size={16} className="text-[#7B8599]" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-7 pt-7 pb-6">{children}</div>
      </div>
    </div>
  );
};

// ── CHART CARD WRAPPER ─────────────────────────────────────────────────────
const ChartCard = ({ title, sub, icon, accent, className = "", children, expandContent }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <div className={`bg-white rounded-2xl border border-[#E4E7F0] shadow-sm p-6 flex flex-col ${className}`}>
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${accent}18` }}>
              {React.createElement(icon, { size: 15, color: accent, strokeWidth: 2.2 })}
            </div>
            <div>
              <div className="text-sm font-bold text-[#0F1623] leading-tight">{title}</div>
              {sub && <div className="text-[11px] text-[#7B8599] mt-0.5">{sub}</div>}
            </div>
          </div>
          <button
            onClick={() => setExpanded(true)}
            title="Expand chart"
            className="w-8 h-8 rounded-lg border border-[#E4E7F0] bg-[#F7F8FC] cursor-pointer flex items-center justify-center flex-shrink-0 transition-all hover:bg-[#EEF2FF] hover:border-[#3D63DD]"
          >
            <Maximize2 size={13} className="text-[#7B8599]" />
          </button>
        </div>
        <div className="flex-1">{children}</div>
      </div>
      <ChartModal open={expanded} onClose={() => setExpanded(false)} title={title} sub={sub} icon={icon} accent={accent}>
        {expandContent || children}
      </ChartModal>
    </>
  );
};

// ── KPI CARD ───────────────────────────────────────────────────────────────
const KPICard = ({ title, value, icon: Icon, accent, sub }) => (
  <div
    className="relative overflow-hidden bg-white rounded-2xl border border-[#E4E7F0] p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md cursor-default"
  >
    <div className="relative flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#7B8599] mb-2">{title}</div>
        <div className="text-[28px] font-extrabold text-[#0F1623] tracking-[-0.03em] leading-none">{value}</div>
        {sub && <div className="text-[11px] text-[#A8B0C0] mt-2">{sub}</div>}
      </div>
      <div className="w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ background: `${accent}14` }}>
        <Icon size={20} color={accent} strokeWidth={2.1} />
      </div>
    </div>
  </div>
);

// ── GUARD STATUS TIMELINE COMPONENT ──────────────────────────────────────
const GuardStatusTimeline = ({ data }) => {
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.filter(entry => entry.status !== 'no_data');
  }, [data]);

  const timelineData = useMemo(() => {
    if (filteredData.length === 0) {
      return { entries: [], totalSlots: 0 };
    }
    return {
      entries: filteredData,
      totalSlots: filteredData.length,
    };
  }, [filteredData]);

  const getStatusColor = (status) => {
    if (status === true || status === 'present') return '#10B981';
    if (status === false || status === 'absent') return '#EF4444';
    return '#D1D5DB';
  };

  const getStatusText = (status) => {
    if (status === true || status === 'present') return 'Present';
    if (status === false || status === 'absent') return 'Absent';
    return 'No Data';
  };

  if (timelineData.totalSlots === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No data available for today
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative pt-4">
        <div className="flex items-end gap-1 overflow-x-auto pb-4 min-h-[120px]">
          {timelineData.entries.map((entry, index) => {
            const color = getStatusColor(entry.status);
            const isPresent = entry.status === true || entry.status === 'present';
            
            return (
              <div
                key={index}
                className="flex flex-col items-center gap-1 group cursor-pointer"
                style={{ minWidth: '32px' }}
              >
                <div 
                  className="rounded-t-lg transition-all duration-200 hover:h-16"
                  style={{ 
                    backgroundColor: color,
                    height: isPresent ? '48px' : '32px',
                    width: '24px',
                    transition: 'all 0.2s ease',
                  }}
                  title={`${dayjs(entry.timestamp).format('HH:mm')} - ${getStatusText(entry.status)}`}
                />
                <div className="text-[10px] text-gray-400 whitespace-nowrap">
                  {dayjs(entry.timestamp).format('HH:mm')}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-center gap-4 text-xs text-gray-600 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-green-500" />
          <span>Present</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-red-500" />
          <span>Absent</span>
        </div>
      </div>
    </div>
  );
};

// ── RECENT ALERTS PANEL ────────────────────────────────────────────────────
const RecentAlerts = ({ rawData }) => {
  const [lightbox, setLightbox] = useState(null);

  const recent5 = useMemo(() => {
    return [...rawData]
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 5);
  }, [rawData]);

  const getAlertMeta = (item) => {
    if (item.type === "person_count") {
      return {
        label: `${item.person_count} person${item.person_count !== 1 ? "s" : ""} detected`,
        accent: C.teal,
        badge: "Occupancy",
        badgeBg: C.tealLight,
        badgeColor: C.teal,
      };
    }

    const hasViolation = (item.ppe_classes || []).some(isViolation);

    return {
      label: (item.ppe_classes || []).map(titleCase).join(", ") || "PPE event",
      accent: hasViolation ? C.danger : C.steel,
      badge: hasViolation ? "Violation" : "PPE",
      badgeBg: hasViolation ? C.dangerLight : C.steelLight,
      badgeColor: hasViolation ? C.danger : C.steel,
    };
  };

  return (
    <>
      <div className="bg-white rounded-2xl border border-[#E4E7F0] shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#EFF1F7]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${C.danger}18` }}>
              <Bell size={15} color={C.danger} strokeWidth={2.2} />
            </div>
            <div>
              <div className="text-sm font-bold text-[#0F1623]">Recent Alerts</div>
              <div className="text-[11px] text-[#7B8599] mt-0.5">Last 5 detection events</div>
            </div>
          </div>
          <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-[#FEE2E2] text-[#EF4444] tracking-[0.05em]">
            LIVE
          </span>
        </div>

        <div className="flex flex-col gap-0">
          {recent5.length === 0 && (
            <div className="py-10 text-center">
              <Empty description="No recent alerts" />
            </div>
          )}
          {recent5.map((item, idx) => {
            const meta = getAlertMeta(item);
            return (
              <div
                key={item._id || idx}
                className={`flex items-center gap-3.5 px-5 py-3.5 transition-colors ${idx < recent5.length - 1 ? 'border-b border-[#EFF1F7]' : ''} ${item.image_url ? 'cursor-pointer hover:bg-[#F7F8FC]' : ''}`}
                onClick={() => item.image_url && setLightbox(item)}
              >
                <div className="w-16 h-13 rounded-lg overflow-hidden flex-shrink-0 bg-[#F7F8FC] border border-[#E4E7F0] relative">
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt="event"
                      className="w-full h-full object-cover"
                      onError={e => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Camera size={18} className="text-[#A8B0C0]" />
                    </div>
                  )}
                  {item.image_url && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 transition-opacity duration-150 hover:opacity-100">
                      <Eye size={14} color="#fff" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[13px] font-bold text-[#0F1623] whitespace-nowrap">
                      {item.camera_id}
                    </span>
                  </div>
                  <div className="text-xs text-[#3D4657] whitespace-nowrap overflow-hidden text-ellipsis">
                    {item.type}
                  </div>
                  <div className="text-[11px] text-[#A8B0C0] mt-0.5">
                    {dayjs(item.timestamp).fromNow()} · {dayjs(item.timestamp).format("h:mm A")}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Lightbox
        open={!!lightbox}
        onClose={() => setLightbox(null)}
        src={lightbox?.image_url}
        alt="alert"
        meta={lightbox}
      />
    </>
  );
};

// ── CAMERA HEATMAP ─────────────────────────────────────────────────────────
const CameraHeatmap = ({ rawData }) => {
  const heatmapData = useMemo(() => {
    if (!rawData.length) return { cameras: [], hours: [], grid: {} };
    const cameras = [...new Set(rawData.map(d => d.camera_id))].sort();
    const hoursSet = new Set();
    const grid = {};

    rawData.forEach(item => {
      const h = dayjs(item.timestamp).format("HH:00");
      hoursSet.add(h);
      const key = `${item.camera_id}__${h}`;
      grid[key] = (grid[key] || 0) + 1;
    });

    const hours = [...hoursSet].sort();
    const maxVal = Math.max(...Object.values(grid), 1);

    return { cameras, hours, grid, maxVal };
  }, [rawData]);

  const getHeatColor = (val, max) => {
    if (!val) return C.surfaceAlt;
    const t = val / max;
    if (t < 0.2) return "#DBEAFE";
    if (t < 0.4) return "#93C5FD";
    if (t < 0.6) return "#3D63DD";
    if (t < 0.8) return "#1D4ED8";
    return "#1E3A8A";
  };

  const getTextColor = (val, max) => {
    if (!val) return C.inkMute;
    const t = val / max;
    return t > 0.4 ? "#fff" : C.ink;
  };

  if (!heatmapData.cameras.length) {
    return (
      <div className="py-10 text-center">
        <Empty description="No heatmap data" />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-1 min-w-[500px]">
        <thead>
          <tr>
            <th className="text-[10px] font-bold text-[#7B8599] text-left px-2 py-1 tracking-[0.06em] uppercase w-[70px]">
              Camera
            </th>
            {heatmapData.hours.map(h => (
              <th key={h} className="text-[10px] font-semibold text-[#7B8599] text-center px-0.5 py-1 min-w-[38px]">
                {h}
              </th>
            ))}
            <th className="text-[10px] font-bold text-[#7B8599] text-center px-2 py-1">Total</th>
          </tr>
        </thead>
        <tbody>
          {heatmapData.cameras.map(cam => {
            const rowTotal = heatmapData.hours.reduce((s, h) => s + (heatmapData.grid[`${cam}__${h}`] || 0), 0);
            return (
              <tr key={cam}>
                <td className="text-[11px] font-bold text-[#3D4657] px-2 py-0.5 whitespace-nowrap">
                  <span className="flex items-center gap-1.5">
                    <Camera size={10} className="text-[#0EA5A8]" />
                    {cam}
                  </span>
                </td>
                {heatmapData.hours.map(h => {
                  const val = heatmapData.grid[`${cam}__${h}`] || 0;
                  return (
                    <td key={h} className="p-0.5">
                      <div
                        title={`${cam} @ ${h}: ${val} detections`}
                        className="h-8 rounded-lg flex items-center justify-center text-[10px] font-bold transition-transform duration-150 hover:scale-115 hover:relative hover:z-10"
                        style={{
                          background: getHeatColor(val, heatmapData.maxVal),
                          color: getTextColor(val, heatmapData.maxVal),
                          minWidth: 32,
                          cursor: val > 0 ? "default" : "default",
                        }}
                      >
                        {val || ""}
                      </div>
                    </td>
                  );
                })}
                <td className="text-center">
                  <span className="text-xs font-extrabold text-[#3D63DD]">{rowTotal}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex items-center gap-2 mt-4 flex-wrap">
        <span className="text-[11px] text-[#7B8599] font-semibold">Intensity:</span>
        {[
          { label: "None", color: C.surfaceAlt, text: C.inkMute },
          { label: "Low", color: "#DBEAFE", text: C.ink },
          { label: "Mid", color: "#93C5FD", text: C.ink },
          { label: "High", color: "#3D63DD", text: "#fff" },
          { label: "Peak", color: "#1E3A8A", text: "#fff" },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1.5">
            <div className="w-5 h-3.5 rounded border border-[#E4E7F0]" style={{ background: l.color }} />
            <span className="text-[10px] text-[#7B8599]">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── DATA PROCESSING ────────────────────────────────────────────────────────
function processPPEData(rawData) {
  const filtered = [...rawData].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  const allClasses = [];
  filtered.forEach(item => (item.ppe_classes || []).forEach(c => allClasses.push(c)));
  const uniqueClassList = [...new Set(allClasses)];
  const classCounts = {};
  allClasses.forEach(c => { classCounts[c] = (classCounts[c] || 0) + 1; });
  const classDistribution = uniqueClassList
    .map(c => ({ name: titleCase(c), rawName: c, value: classCounts[c], color: colorForClass(c, uniqueClassList) }))
    .sort((a, b) => b.value - a.value);

  const cameraStats = {};
  filtered.forEach(item => {
    if (!cameraStats[item.camera_id]) {
      cameraStats[item.camera_id] = { camera_id: item.camera_id, total: 0, classCounts: {}, personCountTotal: 0 };
    }
    cameraStats[item.camera_id].total++;
    (item.ppe_classes || []).forEach(c => {
      cameraStats[item.camera_id].classCounts[c] = (cameraStats[item.camera_id].classCounts[c] || 0) + 1;
    });
    if (typeof item.person_count === "number") {
      cameraStats[item.camera_id].personCountTotal += item.person_count;
    }
  });
  const cameraStatsArr = Object.values(cameraStats);

  const topClasses = classDistribution.slice(0, 3).map(c => c.rawName);
  const hourlyMap = {};
  filtered.forEach(item => {
    const hour = dayjs(item.timestamp).format("YYYY-MM-DD HH:00");
    if (!hourlyMap[hour]) {
      hourlyMap[hour] = { hour, total: 0 };
      topClasses.forEach(tc => (hourlyMap[hour][tc] = 0));
    }
    hourlyMap[hour].total++;
    (item.ppe_classes || []).forEach(c => {
      if (topClasses.includes(c)) hourlyMap[hour][c] = (hourlyMap[hour][c] || 0) + 1;
    });
  });
  const hourlyTrend = Object.values(hourlyMap).sort((a, b) => a.hour.localeCompare(b.hour));
  const uniqueCameras = [...new Set(filtered.map(i => i.camera_id))];

  return {
    totalDetections: filtered.length,
    uniqueCameraCount: uniqueCameras.length,
    uniqueClassCount: uniqueClassList.length,
    mostCommonClass: classDistribution[0]?.name || "—",
    classDistribution,
    cameraStatsArr,
    hourlyTrend,
    topClasses,
    uniqueClassList,
  };
}

function processPersonCountTrend(rawHourly) {
  if (!Array.isArray(rawHourly) || rawHourly.length === 0)
    return { rows: [], peak: 0, peakHour: "—", avg: 0 };
  const rows = rawHourly.map(h => ({
    hour: h.hour,
    hourLabel: dayjs(h.hour, "YYYY-MM-DD HH:mm").isValid()
    ? dayjs(h.hour, "YYYY-MM-DD HH:mm").format("MMM D, h:mm A") : h.hour,
    total: h.total || 0,
    peakBucket: h.buckets?.length ? Math.max(...h.buckets.map(b => b.highestCount || 0)) : 0,
  }));
  const peak = Math.max(...rows.map(r => r.total), 0);
  const peakRow = rows.find(r => r.total === peak);
  const avg = Math.round(rows.reduce((s, r) => s + r.total, 0) / rows.length);
  return { rows, peak, peakHour: peakRow?.hour || "—", avg };
}

// ── REUSABLE CHART RENDERERS ───────────────────────────────────────────────
const PieChartRender = ({ data, height = 260 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <PieChart>
      <Pie data={data.classDistribution} cx="50%" cy="47%" innerRadius={Math.round(height * 0.24)} outerRadius={Math.round(height * 0.35)} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={{ strokeWidth: 1, stroke: C.border }}>
        {data.classDistribution.map((entry, idx) => <Cell key={idx} fill={entry.color} stroke="white" strokeWidth={3} />)}
      </Pie>
      <Tooltip content={<Tip />} />
      <Legend verticalAlign="bottom" height={40} iconType="circle" iconSize={8} formatter={val => <span className="text-[11px] text-[#3D4657]">{val}</span>} />
    </PieChart>
  </ResponsiveContainer>
);

const CameraBarRender = ({ data, height = 260 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data.cameraStatsArr} barGap={4} barCategoryGap="32%">
      <CartesianGrid strokeDasharray="3 3" stroke={C.borderSoft} vertical={false} />
      <XAxis dataKey="camera_id" tick={{ fontSize: 11, fill: C.inkSoft }} tickLine={false} axisLine={false} />
      <YAxis tick={{ fontSize: 11, fill: C.inkSoft }} tickLine={false} axisLine={false} />
      <Tooltip content={<Tip />} />
      <Bar dataKey="total" name="Total Detections" radius={[6, 6, 0, 0]} fill={C.steel} fillOpacity={0.85} />
    </BarChart>
  </ResponsiveContainer>
);

// ── HOURLY AREA CHART ──────────────────────────────────────────────────────
const HourlyAreaRender = ({ data, height = 260 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={data.hourlyTrend}>
      <defs>
        {data.topClasses.map((cls, i) => (
          <linearGradient key={cls} id={`hg-${i}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={colorForClass(cls, data.uniqueClassList)} stopOpacity={0.45} />
            <stop offset="95%" stopColor={colorForClass(cls, data.uniqueClassList)} stopOpacity={0.02} />
          </linearGradient>
        ))}
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke={C.borderSoft} vertical={false} />
      <XAxis
        dataKey="hour"
        tickFormatter={(v) => {
          const d = dayjs(v, "YYYY-MM-DD HH:00");
          return d.isValid() ? d.format("MMM D, ha") : v;
        }}
        tick={{ fontSize: 10, fill: C.inkSoft }}
        tickLine={false}
        axisLine={false}
        interval="preserveStartEnd"
        minTickGap={48}
      />
      <YAxis tick={{ fontSize: 10, fill: C.inkSoft }} tickLine={false} axisLine={false} />
      <Tooltip content={<DateTip />} />
      {data.topClasses.map((cls, i) => (
        <Area
          key={cls}
          type="monotone"
          dataKey={cls}
          name={titleCase(cls)}
          stackId="1"
          stroke={colorForClass(cls, data.uniqueClassList)}
          fill={`url(#hg-${i})`}
          strokeWidth={2}
        />
      ))}
    </AreaChart>
  </ResponsiveContainer>
);

const OccupancyChartRender = ({ personTrend, height = 260 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <ComposedChart data={personTrend.rows}>
      <defs>
        <linearGradient id="ocGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor={C.success} stopOpacity={0.4} />
          <stop offset="95%" stopColor={C.success} stopOpacity={0.02} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke={C.borderSoft} vertical={false} />
      <XAxis
        dataKey="hour"
        tickFormatter={(v) => {
          const d = dayjs(v, ["YYYY-MM-DD HH:mm", "YYYY-MM-DD HH:00"]);
          return d.isValid() ? d.format("MMM D, ha") : v;
        }}
        tick={{ fontSize: 10, fill: C.inkSoft }}
        tickLine={false}
        axisLine={false}
        interval="preserveStartEnd"
        minTickGap={48}
      />
      <YAxis tick={{ fontSize: 11, fill: C.inkSoft }} tickLine={false} axisLine={false} />
      <Tooltip content={<DateTip />} />
      <Area
        type="monotone"
        dataKey="total"
        name="Hourly people"
        stroke={C.success}
        fill="url(#ocGrad)"
        strokeWidth={2.5}
      />
      <Line
        type="monotone"
        dataKey="peakBucket"
        name="Peak 10-min"
        stroke={C.amber}
        strokeWidth={2}
        dot={{ r: 3, fill: C.amber }}
        strokeDasharray="4 2"
      />
    </ComposedChart>
  </ResponsiveContainer>
);

// ── MAIN DASHBOARD ──────────────────────────────────────────────────────────
export default function Dashboard() {
  const [loading, setLoading] = useState(false);
  const [personCountLoading, setPersonCountLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({ personCountData: [],
    overcrowdingCount: 0,
    loiteringCount: 0,
    intrusionCount: 0
   });
  const [personCountGraph, setPersonCountGraph] = useState([]);
  const [personCountTotal, setPersonCountTotal] = useState(0);
  const [receptionStatus, setReceptionStatus] = useState({ reception: null, security: null });
  const [statusLoading, setStatusLoading] = useState(false);
  const [receptionHistory, setReceptionHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const data = useMemo(() => processPPEData(dashboardData.personCountData), [dashboardData.personCountData]);
  const personTrend = useMemo(() => processPersonCountTrend(personCountGraph), [personCountGraph]);

  const fetchPrestigePPKit = async () => {
    try {
      setLoading(true);
      const { data: res } = await axios.post("/fetch-prestigeppkit-dashboard");
      if (res.success) setDashboardData({ 
        personCountData: res.personCountData || [], 
        overcrowdingCount: res.overcrowdingCount || 0 ,
        loiteringCount: res.loiteringCount || 0,
        intrusionCount: res.intrusionCount || 0
        });
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchPrestigePersonCount = async () => {
    try {
      setPersonCountLoading(true);
      const { data: res } = await axios.post("/fetch-prestige-person-count");
      if (res.success) { setPersonCountGraph(res.data || []); setPersonCountTotal(res.total || 0); }
    } catch (e) { console.error(e); } finally { setPersonCountLoading(false); }
  };

  const fetchReceptionStatus = async () => {
    try {
      setStatusLoading(true);
      const { data: res } = await axios.post("/get-reception-status");
      if (res.success) {
        setReceptionStatus(res.data);
      }
    } catch (e) {
      console.error("Error fetching reception status:", e);
    } finally {
      setStatusLoading(false);
    }
  };

  const fetchReceptionHistory = async () => {
    try {
      setHistoryLoading(true);
      const { data: res } = await axios.get("/get-reception-history");
      if (res.success) {
        setReceptionHistory(res.data.reception || []);
      }
    } catch (e) {
      console.error("Error fetching reception history:", e);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchPrestigePPKit();
    fetchPrestigePersonCount();
    fetchReceptionStatus();
    fetchReceptionHistory();
    
    const interval = setInterval(fetchPrestigePersonCount, 60000);
    const statusInterval = setInterval(fetchReceptionStatus, 30000);
    const historyInterval = setInterval(fetchReceptionHistory, 120000);
    
    return () => {
      clearInterval(interval);
      clearInterval(statusInterval);
      clearInterval(historyInterval);
    };
  }, []);

  const formatCameraName = (name) => {
    if (!name) return "—";
    return name.replace(/_/g, " ");
  };

  const StatusCard = ({ title, data, icon: Icon, accent }) => {
    const isPresent = data?.status === true;
    const statusText = data ? (isPresent ? "Present" : "Absent") : "No Data";
    const statusColor = data ? (isPresent ? C.success : C.danger) : C.inkMute;
    const cameraName = data ? formatCameraName(data.camera_name) : "—";

    return (
      <div className="relative overflow-hidden bg-white rounded-2xl border border-[#E4E7F0] p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md cursor-default">
        <div className="relative flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#7B8599] mb-2">{title}</div>
            
            <div className="flex items-center gap-2 mb-1.5">
              <span
                className="w-2 h-2 rounded-full inline-block"
                style={{ 
                  background: statusColor,
                  animation: isPresent ? "pulse 2s infinite" : "none"
                }}
              />
              <div className="text-2xl font-extrabold tracking-[-0.03em] leading-none" style={{ color: statusColor }}>
                {statusText}
              </div>
            </div>
            
            <div className="text-sm font-semibold text-[#3D4657] mt-0.5">
              {cameraName}
            </div>
          </div>
          
          <div className="w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ background: `${accent}14` }}>
            <Icon size={20} color={accent} strokeWidth={2.1} />
          </div>
        </div>
      </div>
    );
  };

  const cameraColumns = [
    {
      title: "Camera", dataIndex: "camera_id", key: "camera_id", fixed: "left",
      render: text => (
        <span className="flex items-center gap-2 font-semibold text-[#0F1623]">
          <span className="w-7 h-7 rounded-lg bg-[#EEF2FF] flex items-center justify-center">
            <Camera size={12} className="text-[#3D63DD]" />
          </span>
          {text}
        </span>
      ),
    },
    ...data.uniqueClassList.map(cls => ({
      title: <span className="text-[11px] font-semibold">{titleCase(cls)}</span>,
      key: cls, align: "center",
      render: (_, row) => {
        const count = row.classCounts[cls] || 0;
        return count > 0 ? (
          <span className={`inline-block text-[11px] font-bold px-2.5 py-0.5 rounded-full ${isViolation(cls) ? 'bg-[#FEE2E2] text-[#EF4444]' : 'bg-[#EEF2FF] text-[#3D63DD]'}`}>
            {count}
          </span>
        ) : <span className="text-[#A8B0C0]">–</span>;
      },
    })),
    {
      title: <span className="text-[11px] font-semibold">Persons</span>,
      dataIndex: "personCountTotal", key: "personCountTotal", align: "center",
      render: val => <span className="inline-block text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-[#EDE9FE] text-[#8B5CF6]">{val}</span>,
    },
    {
      title: <span className="text-[11px] font-semibold">Total</span>,
      dataIndex: "total", key: "total", align: "center",
      render: val => <span className="text-sm font-extrabold text-[#3D63DD]">{val}</span>,
    },
  ];

  return (
    <div className="flex flex-col gap-6 p-6 bg-slate-50 min-h-screen">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px) scale(0.98) } to { opacity: 1; transform: translateY(0) scale(1) } }
        
        @keyframes pulse {
          0% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.2); }
          100% { opacity: 1; transform: scale(1); }
        }

        .ppe-table .ant-table {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 13px;
        }
        .ppe-table .ant-table-thead > tr > th {
          background: #F7F8FC;
          font-size: 11px;
          font-weight: 700;
          color: #7B8599;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          border-bottom: 1px solid #E4E7F0;
          padding: 10px 16px;
        }
        .ppe-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #EFF1F7;
          padding: 11px 16px;
          vertical-align: middle;
        }
        .ppe-table .ant-table-tbody > tr:hover > td {
          background: #EEF2FF !important;
        }
        .ppe-table .ant-table-tbody > tr:last-child > td { border-bottom: none; }
        .ppe-table .ant-table-pagination { padding: 12px 16px; }
        .ppe-table .ant-table-pagination-item-active { background: #3D63DD !important; border-color: #3D63DD !important; }
        .ppe-table .ant-table-pagination-item-active a { color: white !important; }
      `}</style>

      {/* ── HEADER ── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-xl bg-[#3D63DD] flex items-center justify-center shadow-[0_4px_14px_#3D63DD44]">
            <ShieldAlert size={22} color="#fff" strokeWidth={2.1} />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-[22px] font-bold text-[#0F1623] m-0 tracking-[-0.03em]">Analytics Dashboard</h1>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-[#D1FAE5] text-[#10B981]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
                LIVE
              </span>
            </div>
            <div className="text-xs text-[#7B8599] mt-0.5">
              {data.uniqueCameraCount} cameras · {data.uniqueClassCount} PPE classes · auto-refreshes every minute
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <div className="text-xs font-semibold px-4 py-2 rounded-lg border border-[#E4E7F0] bg-white text-[#7B8599]">
            {dayjs().format("ddd, MMM D · h:mm A")}
          </div>
          <Button
            onClick={() => { fetchPrestigePPKit(); fetchPrestigePersonCount(); fetchReceptionStatus(); fetchReceptionHistory(); }}
            loading={loading || personCountLoading}
            icon={<RefreshCw size={14} />}
            className="!bg-[#3D63DD] !text-white !border-none !rounded-lg !h-[38px] !font-semibold !text-[13px]"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* ── SECTION 1: KPI STRIP ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPICard title="Total Detections" value={data.totalDetections.toLocaleString()} icon={Layers} accent={C.steel} sub="events logged" />
        <KPICard title="Active Cameras" value={data.uniqueCameraCount} icon={Camera} accent={C.teal} sub="Reporting now" />
        <KPICard title="PPE Classes" value={data.uniqueClassCount} icon={HardHat} accent={C.violet} sub="Distinct types tracked" />
        <KPICard title="People (6h)" value={personCountTotal.toLocaleString()} icon={Users} accent={C.amber} sub={`Peak: ${personTrend.peakHour}`} />
      </div>

      {/* ── NEW: ADDITIONAL SECURITY METRICS ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPICard 
          title="Intrusion Alerts" 
          value={dashboardData.intrusionCount || 0} 
          icon={ShieldAlert} 
          accent={C.danger} 
          sub={dashboardData.intrusionCount === 0 ? "No intrusions detected" : `${dashboardData.intrusionCount} intrusion event(s)`} 
        />
        <KPICard 
          title="Loitering" 
          value={dashboardData.loiteringCount || 0} 
          icon={AlertTriangle} 
          accent="#8B5CF6" 
          sub={dashboardData.loiteringCount === 0 ? "No loitering detected" : `${dashboardData.loiteringCount} loitering event(s)`} 
        />
        <KPICard 
          title="Overcrowding" 
          value={dashboardData.overcrowdingCount || 0} 
          icon={Users} 
          accent={C.amber} 
          sub={dashboardData.overcrowdingCount === 0 ? "No overcrowding events" : `${dashboardData.overcrowdingCount} overcrowding event(s)`} 
        />
        <KPICard title="Fire Alerts" value={0} icon={Activity} accent="#F97316" sub="No fire threats detected" />
      </div>

      {/* ── NEW: ADDITIONAL DUMMY KPI CARDS ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPICard 
          title="Fall Detection" 
          value={0} 
          icon={AlertTriangle} 
          accent="#EF4444" 
          sub="No falls detected" 
        />
        <KPICard 
          title="Fight Detection" 
          value={0} 
          icon={Activity} 
          accent="#F97316" 
          sub="No fights detected" 
        />
        <KPICard 
          title="Unauthorized Access" 
          value={0} 
          icon={ShieldAlert} 
          accent="#DC2626" 
          sub="All access authorized" 
        />
        <KPICard 
          title="Suspicious Activity" 
          value={0} 
          icon={Eye} 
          accent="#7C3AED" 
          sub="No suspicious activity" 
        />
      </div>

      {/* ── RECEPTION & SECURITY STATUS CARDS ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatusCard 
          title="Reception" 
          data={receptionStatus.reception} 
          icon={Users} 
          accent={C.steel} 
        />
        <StatusCard 
          title="Security" 
          data={receptionStatus.security} 
          icon={ShieldAlert} 
          accent={C.danger} 
        />
      </div>

      {/* ── GUARD ATTENDANCE TIMELINE ── */}
      <div className="bg-white rounded-2xl border border-[#E4E7F0] p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${C.steel}18` }}>
            <Shield size={15} color={C.steel} strokeWidth={2.2} />
          </div>
          <div>
            <div className="text-sm font-bold text-[#0F1623]">Reception Attendance Timeline</div>
            <div className="text-[11px] text-[#7B8599] mt-0.5">15-minute intervals showing reception presence throughout the day</div>
          </div>
        </div>
        
        {historyLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Loading timeline...</p>
          </div>
        ) : (
          <GuardStatusTimeline data={receptionHistory} />
        )}
      </div>

      {/* ── SECTION 2: THREE CHARTS ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        <ChartCard title="PPE Class Distribution" sub="All detections by type" icon={HardHat} accent={C.violet}
          expandContent={data.classDistribution.length === 0 ? <Empty description="No data" /> : <PieChartRender data={data} height={420} />}
        >
          {data.classDistribution.length === 0 ? <Empty description="No data" className="py-10" /> : <PieChartRender data={data} height={260} />}
        </ChartCard>

        <ChartCard title="Detections by Camera" sub="Total detections per camera" icon={Camera} accent={C.steel}
          expandContent={data.cameraStatsArr.length === 0 ? <Empty description="No data" /> : <CameraBarRender data={data} height={420} />}
        >
          {data.cameraStatsArr.length === 0 ? <Empty description="No data" className="py-10" /> : <CameraBarRender data={data} height={260} />}
        </ChartCard>

        <ChartCard title="Hourly Detection Trend" sub="Top 3 classes over time" icon={Clock} accent={C.teal}
          expandContent={data.hourlyTrend.length === 0 ? <Empty description="No data" /> : <HourlyAreaRender data={data} height={420} />}
        >
          {data.hourlyTrend.length === 0 ? <Empty description="No data" className="py-10" /> : <HourlyAreaRender data={data} height={260} />}
        </ChartCard>
      </div>

      {/* ── SECTION 4: RECENT ALERTS + HEATMAP ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-1">
          <RecentAlerts rawData={dashboardData.personCountData} />
        </div>
      
        <div className="xl:col-span-2 bg-white rounded-2xl border border-[#E4E7F0] shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-6 py-5 border-b border-[#EFF1F7]">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${C.steel}18` }}>
              <Thermometer size={15} color={C.steel} strokeWidth={2.2} />
            </div>
            <div>
              <div className="text-sm font-bold text-[#0F1623]">Detection Heatmap</div>
              <div className="text-[11px] text-[#7B8599] mt-0.5">Camera activity by hour — darker = more detections</div>
            </div>
          </div>
          <div className="px-6 py-5">
            <CameraHeatmap rawData={dashboardData.personCountData} />
          </div>
        </div>
      </div>

      {/* ── SECTION 3: OCCUPANCY + SNAPSHOT ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2">
          <ChartCard title="Live Occupancy Trend" sub="Headcount over last 6 hours" icon={Activity} accent={C.success}
            expandContent={
              personTrend.rows.length === 0 ? <Empty description="No occupancy data" /> :
              <>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[
                    { label: "People (6h total)", value: personCountTotal, color: C.success },
                    { label: "Peak hour", value: personTrend.peakHour, color: C.amber },
                    { label: "Hourly avg", value: personTrend.avg, color: C.teal },
                  ].map(m => (
                    <div key={m.label} className="rounded-xl px-4 py-3.5 bg-[#F7F8FC]">
                      <div className="text-2xl font-extrabold leading-none" style={{ color: m.color }}>{m.value}</div>
                      <div className="text-[11px] text-[#7B8599] mt-1.5">{m.label}</div>
                    </div>
                  ))}
                </div>
                <OccupancyChartRender personTrend={personTrend} height={360} />
              </>
            }
          >
            <div className="grid grid-cols-3 gap-2.5 mb-4">
              {[
                { label: "6h total", value: personCountTotal, color: C.success },
                { label: "Peak", value: personTrend.peakHour, color: C.amber },
                { label: "Avg/hr", value: personTrend.avg, color: C.teal },
              ].map(m => (
                <div key={m.label} className="rounded-lg px-3.5 py-2.5 bg-[#F7F8FC]">
                  <div className="text-lg font-extrabold leading-none" style={{ color: m.color }}>{m.value}</div>
                  <div className="text-[10px] text-[#7B8599] mt-1">{m.label}</div>
                </div>
              ))}
            </div>
            {personTrend.rows.length === 0
              ? <Empty description="No occupancy data" className="py-8" />
              : <OccupancyChartRender personTrend={personTrend} height={200} />
            }
          </ChartCard>
        </div>

        {/* Site Snapshot */}
        <div className="bg-white rounded-2xl border border-[#E4E7F0] p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${C.amber}18` }}>
              <Sparkles size={15} color={C.amber} strokeWidth={2.2} />
            </div>
            <div>
              <div className="text-sm font-bold text-[#0F1623]">Site Snapshot</div>
              <div className="text-[11px] text-[#7B8599] mt-0.5">Current session stats</div>
            </div>
          </div>
          <div className="flex flex-col gap-2.5">
            {[
              { label: "Most detected", value: data.mostCommonClass, icon: Eye, color: C.steel },
              { label: "Cameras live", value: data.uniqueCameraCount, icon: Camera, color: C.teal },
              { label: "PPE classes", value: data.uniqueClassCount, icon: HardHat, color: C.violet },
              { label: "Total detections", value: data.totalDetections.toLocaleString(), icon: Layers, color: C.steel },
              { label: "People today (6h)", value: personCountTotal, icon: Users, color: C.amber },
              { label: "Peak occupancy", value: personTrend.peak, icon: Activity, color: C.success },
            ].map(m => {
              const MIcon = m.icon;
              return (
                <div key={m.label} className="flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-[#F7F8FC]">
                  <div className="flex items-center gap-2.5">
                    <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${m.color}18` }}>
                      <MIcon size={13} color={m.color} />
                    </span>
                    <span className="text-xs font-medium text-[#3D4657]">{m.label}</span>
                  </div>
                  <span className="text-[13px] font-extrabold" style={{ color: m.color }}>{m.value}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── SECTION 5: CAMERA TABLE ── */}
      <div className="bg-white rounded-2xl border border-[#E4E7F0] overflow-hidden shadow-sm">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-[#EFF1F7]">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${C.steel}18` }}>
            <BarChart3 size={15} color={C.steel} strokeWidth={2.2} />
          </div>
          <div>
            <div className="text-sm font-bold text-[#0F1623]">Camera Breakdown</div>
            <div className="text-[11px] text-[#7B8599] mt-0.5">Detections by camera and PPE class</div>
          </div>
        </div>
        <Table
          dataSource={data.cameraStatsArr}
          columns={cameraColumns}
          rowKey="camera_id"
          pagination={{ pageSize: 8, size: "small" }}
          size="small"
          bordered={false}
          scroll={{ x: true }}
          className="ppe-table"
          locale={{ emptyText: <Empty description="No camera data" className="py-10" /> }}
        />
      </div>
    </div>
  );
}